/**
 * The review workflow a report moves through, and who may move it.
 *
 * Pure rules: no framework, no network, no store. The same state machine runs
 * on the server, and this copy exists so the interface can disable a move
 * rather than offer one that will be refused.
 *
 * That duplication is deliberate and one-directional. **The server decides.**
 * Nothing here is a permission check; it is a prediction of one, used to keep
 * the interface honest. Anything this allows that the server refuses becomes a
 * rejected request, which is the correct outcome. Anything this forbids that
 * the server would allow is a move a user cannot make, which is a bug in this
 * file and not a hole. Its tests exist to keep the two descriptions the same.
 */

/** Where a report stands. */
export type WorkflowState = 'brouillon' | 'en_revue' | 'valide' | 'diffuse' | 'rejete'

/** How one person relates to one report. */
export type DraftRole = 'owner' | 'commenter' | 'reviewer' | 'validateur'

export const WORKFLOW_STATES: readonly WorkflowState[] = [
  'brouillon',
  'en_revue',
  'valide',
  'diffuse',
  'rejete',
]

/**
 * Which states each state may move to. Anything absent is refused.
 *
 * Keyed by plain `string` rather than `WorkflowState`, because the current
 * state arrives from the API and may have been written by a version that knew
 * a state this one does not. An unknown key finds no targets, so every move
 * out of it is refused, which is the right answer.
 */
const TRANSITIONS: Readonly<Record<string, readonly WorkflowState[]>> = {
  brouillon: ['en_revue'],
  en_revue: ['valide', 'rejete'],
  valide: ['diffuse', 'brouillon'],
  diffuse: ['brouillon'],
  rejete: ['brouillon'],
}

/** Roles the identity provider may assert that carry approval authority. */
const APPROVER_ROLES: ReadonlySet<string> = new Set(['DAF', 'Direction Generale'])

/** Moves that need approval authority rather than mere authorship. */
const APPROVER_TARGETS: ReadonlySet<string> = new Set(['valide', 'rejete', 'diffuse', 'brouillon'])

/**
 * The document may only be edited in these states. Everywhere else the content
 * is fixed and only the workflow can change.
 */
const EDITABLE_STATES: ReadonlySet<string> = new Set(['brouillon', 'rejete'])

/**
 * Roles whose holders may change what a report contains. A commenter can
 * discuss the figures and an approver can sign them off, but neither of those
 * is a mandate to import a balance or rewrite a note.
 */
const CONTRIBUTOR_ROLES: ReadonlySet<string> = new Set<DraftRole>(['owner', 'reviewer'])

export const INITIAL_STATE: WorkflowState = 'brouillon'

/** Whether a string names a state this version knows. */
export function isWorkflowState(value: string): value is WorkflowState {
  return (WORKFLOW_STATES as readonly string[]).includes(value)
}

/** States reachable from here, ignoring who is asking. */
export function legalTargets(state: string): readonly WorkflowState[] {
  return TRANSITIONS[state] ?? []
}

/** Whether the content may be changed in this state, whoever is asking. */
export function isEditable(state: string): boolean {
  return EDITABLE_STATES.has(state)
}

/**
 * Whether this person may change the report's contents right now.
 *
 * Two conditions, and both must hold: the report has to be in a state where
 * its content is still open, and the person has to hold a role that
 * contributes rather than one that observes or approves.
 */
export function mayEditContent(role: DraftRole | null, state: string): boolean {
  return role !== null && isEditable(state) && CONTRIBUTOR_ROLES.has(role)
}

/** One coherence check computed over the report's figures. */
export interface ControlOutcome {
  readonly code: string
  readonly label: string
  readonly severity: 'blocking' | 'warning'
  readonly ok: boolean
}

/** Controls that must pass before sign-off and have not. */
export function blockingFailures(controls: readonly ControlOutcome[]): readonly ControlOutcome[] {
  return controls.filter((control) => control.severity === 'blocking' && !control.ok)
}

/** Who is attempting a move, and what standing they hold. */
export interface Mover {
  readonly isOwner: boolean
  /** Roles the identity provider asserted for this person. */
  readonly roles: ReadonlySet<string>
  /**
   * Named an approver on this specific report, and has accepted. A pending
   * invitation confers nothing.
   */
  readonly isDraftApprover: boolean
}

/** Whether a role the identity provider asserted carries approval authority. */
export function hasApproverRole(roles: ReadonlySet<string>): boolean {
  for (const role of roles) {
    if (APPROVER_ROLES.has(role)) return true
  }
  return false
}

/**
 * Whether this mover has standing for this particular move.
 *
 * Authors are trusted with their own work: submitting it for review, and
 * picking it back up after it was rejected. Everything else, approving,
 * rejecting, issuing, and reopening something already approved or issued,
 * needs approval authority, held either globally or on this report.
 */
function hasStanding(current: string, target: string, mover: Mover): boolean {
  // Submitting is open to anyone who can edit the report.
  if (target === 'en_revue') return true

  // Taking back your own rejected work.
  if (current === 'rejete' && target === 'brouillon') {
    return mover.isOwner || hasApproverRole(mover.roles) || mover.isDraftApprover
  }

  if (APPROVER_TARGETS.has(target)) {
    return hasApproverRole(mover.roles) || mover.isDraftApprover
  }

  return false
}

/**
 * Why a move was not allowed.
 *
 * A reason rather than a bare boolean, because the three cases call for
 * different things to be shown. A disabled control with no explanation is the
 * most common way an interface wastes somebody's afternoon.
 *
 *   illegal       the move is not part of the state machine
 *   unauthorised  the mover lacks standing
 *   blocked       the move is legal and permitted, but a precondition of the
 *                 report itself has not been met
 */
export interface Refusal {
  readonly kind: 'illegal' | 'unauthorised' | 'blocked'
  readonly reason: string
}

/**
 * Decide whether a move may proceed. `null` means yes.
 *
 * Checks run cheapest and most fundamental first: is the move part of the
 * machine, does the mover have standing, and only then whether the report is
 * in a fit state to be signed off.
 */
export function checkTransition(
  current: string,
  target: string,
  mover: Mover,
  controls: readonly ControlOutcome[] = [],
): Refusal | null {
  if (!legalTargets(current).some((state) => state === target)) {
    return { kind: 'illegal', reason: `cannot move from '${current}' to '${target}'` }
  }

  if (!hasStanding(current, target, mover)) {
    const held = [...mover.roles].sort().join(', ') || 'none'
    return {
      kind: 'unauthorised',
      reason: `not permitted to move this report to '${target}' (roles held: ${held})`,
    }
  }

  if (target === 'valide') {
    const failed = blockingFailures(controls)
    if (failed.length > 0) {
      const listed = failed.map((control) => `${control.code} ${control.label}`.trim()).join(', ')
      return { kind: 'blocked', reason: `blocking controls failed: ${listed}` }
    }
  }

  return null
}

/** Whether this person may make this move. */
export function mayTransition(
  current: string,
  target: string,
  mover: Mover,
  controls: readonly ControlOutcome[] = [],
): boolean {
  return checkTransition(current, target, mover, controls) === null
}

/** The moves this person may actually make from here. */
export function availableTransitions(
  current: string,
  mover: Mover,
  controls: readonly ControlOutcome[] = [],
): readonly WorkflowState[] {
  return legalTargets(current).filter((target) => mayTransition(current, target, mover, controls))
}
